import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios from '@/portainer/services/axios/axios';
import { withError } from '@/react-tools/react-query';
import { getAllSettledItems } from '@/portainer/helpers/promise-utils';

import { SilenceData } from './silenceTypes';

export interface CreateSilencePayload {
  alertManagerURL: string;
  silence: {
    comment: string;
    createdBy: string;
    startsAt: string;
    endsAt: string;
    matchers: Array<{
      name: string;
      value: string;
      isRegex: boolean;
      isEqual?: boolean;
    }>;
  };
}

export interface CreateSilenceResponse {
  message: string;
  silence: {
    id: string;
    comment: string;
    createdBy: string;
    startsAt: string;
    endsAt: string;
    matchers: Array<{
      name: string;
      value: string;
      isRegex: boolean;
      isEqual?: boolean;
    }>;
  };
}

export async function createSilences(
  silenceData: SilenceData[],
  alertManagerURL: string = 'localhost:9093'
) {
  return getAllSettledItems(silenceData, createSilence);

  async function createSilence(silenceData: SilenceData) {
    // Convert datetime-local strings to ISO format for the backend
    const now = new Date();
    const startTime = new Date(silenceData.startsAt);
    const endTime = new Date(silenceData.endsAt);
    const intendedDuration = endTime.getTime() - startTime.getTime();
    const adjustedStartTime =
      startTime < now ? new Date(now.getTime() + 1000) : startTime;

    const adjustedEndTime =
      startTime < now
        ? new Date(adjustedStartTime.getTime() + intendedDuration)
        : endTime;

    const finalEndTime =
      adjustedEndTime <= adjustedStartTime
        ? new Date(
            adjustedStartTime.getTime() + Math.max(intendedDuration, 60 * 1000)
          )
        : adjustedEndTime;

    const startsAt = adjustedStartTime.toISOString();
    const endsAt = finalEndTime.toISOString();

    const payload: CreateSilencePayload = {
      alertManagerURL,
      silence: {
        comment: silenceData.comment,
        createdBy: silenceData.createdBy,
        startsAt,
        endsAt,
        matchers: silenceData.matchers,
      },
    };

    const { data } = await axios.post<CreateSilenceResponse>(
      '/observability/alerting/silence',
      payload
    );

    return data;
  }
}

export function useCreateSilence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      silenceData,
      alertManagerURL,
    }: {
      silenceData: SilenceData[];
      alertManagerURL?: string;
    }) => createSilences(silenceData, alertManagerURL),
    onSuccess: () => {
      // Invalidate active alerts and silenced alerts queries to refresh the UI
      queryClient.invalidateQueries(['alerts', 'active']);
      queryClient.invalidateQueries(['alerts', 'silenced']);
    },
    ...withError('Failed to create alert silence'),
  });
}
